import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAgentGroups } from '@/features/agent-groups/api';
import { useMemo } from 'react';
import { templateLabel, type TtsFlowFormValues } from '../../types';
import { TtsFlowPreviewPane } from './TtsFlowPreviewPane';

interface Props {
  values: TtsFlowFormValues;
  /** Preview pane caches synthesized previews back onto the rows. */
  setValues: (patch: Partial<TtsFlowFormValues>) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

export function StepReview({ values, setValues }: Props) {
  const groups = useAgentGroups();

  /** Group id → name (for patch summary). */
  const groupLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups.data ?? []) map.set(String(g.groupId), g.groupName);
    return (id: string) => map.get(id) ?? id;
  }, [groups.data]);

  void groupLabel; // patch list already carries the friendly name.

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      {/* Left — summary */}
      <Card>
        <CardHeader>
          <CardTitle>Review</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="rounded-md border border-border bg-card px-3 py-1">
            <Row label="Name" value={values.ttsFlowName} />
            <Row
              label="Template"
              value={values.templateId ? templateLabel(values.templateId) : ''}
            />
            <Row label="Language" value={values.language} />
            <Row label="Voice" value={values.gender} />
            <Row
              label="TTS rows"
              value={
                values.ttsRows.length === 0
                  ? ''
                  : `${values.ttsRows.length} step${values.ttsRows.length === 1 ? '' : 's'} — preview on the right`
              }
            />
        
            
           
          </dl>
        </CardContent>
      </Card>

      {/* Right — live caller-flow preview (same pane as the rows step) */}
      <div className="lg:sticky lg:top-2 lg:self-start">
        <TtsFlowPreviewPane
          values={values}
          onPatchRow={(i, patch) =>
            setValues({
              ttsRows: values.ttsRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
            })
          }
        />
      </div>
    </div>
  );
}
